import {
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import {
  IOutsourcedOrder,
  IOutsourcedOrdersQueryParams,
  IOutsourcedOrdersSummary
} from './interfaces';
import { getOutsourcedOrdersByStore } from '../../services/outsourcingOrder.service';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';

const listQueryKey = 'outsourcingOrders';

export interface IOutsourcedOrdersQueryResult {
  data: IOutsourcedOrder[];
  pagination: {
    totalDocs: number,
    limit: number,
    page: number,
    totalPages: number,
  },
  summary?: IOutsourcedOrdersSummary,
}

export const useGetOutsourcedOrders = (enabled: boolean, query: IOutsourcedOrdersQueryParams) => useInfiniteQuery<IOutsourcedOrdersQueryResult>({
  staleTime: 0,
  enabled: enabled,
  queryKey: [
    listQueryKey,
    query.storeId,
    query.statuses,
    query.page,
    query.limit,
    query.sequence,
    query.partnerId,
    query.dateFrom,
    query.dateTo,
    query.dateField,
    query.includeSummary,
    query.sortBy,
    query.sortDirection,
  ],
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.data.length, 0) || 0;
    const lastPageValue = (lastPage.pagination && lastPage.pagination.totalDocs) || 0;
    if (lastPageValue <= currentRows) {
      return undefined
    }
    return lastPageParam + 1;
  },
  getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({ queryKey, pageParam }: { queryKey: any, pageParam: number }) => {
    try {
      const [_key] = queryKey;
      const res = await getOutsourcedOrdersByStore({
        ...query,
        page: query.page || pageParam,
        limit: query.limit,
        sequence: query.sequence
      });

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IOutsourcedOrdersQueryResult;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetOutsourcedOrdersReport = (
  enabled: boolean,
  query: IOutsourcedOrdersQueryParams
) => useQuery<IOutsourcedOrdersQueryResult>({
  staleTime: 0,
  enabled,
  queryKey: [
    listQueryKey,
    'report',
    query.storeId,
    query.statuses,
    query.page,
    query.limit,
    query.sequence,
    query.partnerId,
    query.dateFrom,
    query.dateTo,
    query.dateField,
    query.includeSummary,
    query.sortBy,
    query.sortDirection,
  ],
  queryFn: async () => {
    try {
      const res = await getOutsourcedOrdersByStore(query);

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IOutsourcedOrdersQueryResult;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});
